<script setup>
import { vInline } from "~/composables/useInlineEdit";

const props = defineProps({ data: { type: Object, required: true } });

const localePath = useLocalePath();
// External URLs link out as-is; site-relative URLs get the active locale prefix.
const isExternal = computed(() => /^https?:\/\//i.test(props.data.url));
const to = computed(() => (isExternal.value ? props.data.url : localePath(props.data.url)));
</script>

<template>
  <section class="section">
    <div class="container-x">
      <div
        class="relative isolate overflow-hidden rounded-[2.5rem] px-8 py-16 md:px-16 md:py-20
               bg-gradient-to-br from-brand-green to-[#56A832] text-paper shadow-verdant"
      >
        <!-- Concentric arcs echo the logo; crisp rings read cleaner than soft color blurs. -->
        <svg
          aria-hidden="true"
          class="pointer-events-none absolute -right-20 -top-24 h-[24rem] w-[24rem] text-paper/10"
          viewBox="0 0 200 200" fill="none"
        >
          <circle cx="100" cy="100" r="96" stroke="currentColor" stroke-width="2" />
          <circle cx="100" cy="100" r="68" stroke="currentColor" stroke-width="2" />
          <circle cx="100" cy="100" r="40" stroke="currentColor" stroke-width="2" />
        </svg>
        <svg
          aria-hidden="true"
          class="pointer-events-none absolute -bottom-28 -left-24 h-72 w-72 text-paper/[0.07]"
          viewBox="0 0 200 200" fill="none"
        >
          <circle cx="100" cy="100" r="96" stroke="currentColor" stroke-width="2" />
          <circle cx="100" cy="100" r="60" stroke="currentColor" stroke-width="2" />
        </svg>

        <div class="relative flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <div class="max-w-2xl space-y-5">
            <div class="rainbow-rule" />
            <h2 class="font-display font-bold text-paper text-balance" v-inline="'heading'">
              {{ data.heading }}
            </h2>
          </div>
          <NuxtLink
            :to="to"
            :external="isExternal"
            class="group inline-flex shrink-0 items-center gap-2 rounded-full bg-paper px-6 py-3 sm:px-7 sm:py-3.5
                   font-semibold text-brand-green no-underline text-center
                   shadow-[0_12px_32px_-12px_rgba(0,0,0,0.4)]
                   transition-all duration-200 ease-out
                   hover:-translate-y-0.5 hover:shadow-[0_18px_44px_-12px_rgba(0,0,0,0.5)]
                   focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper"
          >
            {{ data.button_label }}
            <svg class="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M4 10h12m0 0l-4-4m4 4l-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </NuxtLink>
        </div>
      </div>
    </div>
  </section>
</template>
